import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Flex, HStack, Link, Text } from '@chakra-ui/react';
import { AlertCircle, ArrowRight, Check } from 'lucide-react';
import { formatMoney } from '../../content/site';
import { useSiteConfig } from '../../lib/siteConfig';
import { useT } from '../../i18n';
import { useMonthName } from './CostsCard';

// Under the landing page's hero: this month's figures, one line, then the
// full costs (M55, docs/run-by-families.md D195). Hidden until a month is
// published. The numbers are the public GET /api/site's (D113, D196).
export function RunByFamilies() {
  const costs = useSiteConfig()?.costs;
  const t = useT();
  const monthName = useMonthName();
  if (!costs) return null;

  const count = costs.familiesCount ?? 0;
  const standing =
    costs.balance >= 0
      ? costs.coveredThrough && costs.coveredThrough !== costs.month
        ? t('site.costs.coveredThrough', {
            month: monthName(costs.coveredThrough),
          })
        : t('site.costs.covered')
      : t('site.runBy.short', { amount: formatMoney(-costs.balance) });
  const figures = [
    monthName(costs.month),
    t('site.runBy.costs', { amount: formatMoney(costs.total) }),
    t('site.runBy.received', { amount: formatMoney(costs.received) }),
    ...(count > 0
      ? [
          t(
            count === 1 ? 'site.runBy.familiesOne' : 'site.runBy.familiesOther',
            { count }
          ),
        ]
      : []),
  ];

  return (
    <Box
      as="section"
      maxW="1080px"
      mx="auto"
      px={4}
      pt={{ base: 10, md: 14 }}
      aria-label={t('site.runBy.label')}
    >
      <Box
        bg="surface"
        borderWidth="1px"
        borderColor="line"
        borderRadius="card"
        px={{ base: 4, md: 6 }}
        py={{ base: 4, md: 5 }}
      >
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 6 }}
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
        >
          <Box minW={0}>
            <Text fontSize={{ base: '18px', md: '20px' }} fontWeight={800}>
              {t('site.runBy.title')}
            </Text>
            <Text color="ink.body" fontSize="15px" pt={1}>
              {figures.map((figure, index) => (
                <Fragment key={figure}>
                  {index > 0 && (
                    <Text as="span" color="muted" aria-hidden="true">
                      {' · '}
                    </Text>
                  )}
                  <Text
                    as="span"
                    whiteSpace="nowrap"
                    fontWeight={index === 0 ? 700 : 500}
                  >
                    {figure}
                  </Text>
                </Fragment>
              ))}
            </Text>
            <HStack
              spacing={1.5}
              pt={1}
              color={costs.balance >= 0 ? 'accent' : 'danger'}
            >
              {costs.balance >= 0 ? (
                <Check size={16} aria-hidden="true" />
              ) : (
                <AlertCircle size={16} aria-hidden="true" />
              )}
              <Text fontSize="15px" fontWeight={700}>
                {standing}
              </Text>
            </HStack>
          </Box>
          <Link
            as={RouterLink}
            to="/pricing"
            color="accent"
            fontWeight={700}
            whiteSpace="nowrap"
            display="inline-flex"
            alignItems="center"
            gap={1}
          >
            {t('site.runBy.link')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </Flex>
      </Box>
    </Box>
  );
}
